import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Modal, VbButton, Callout } from "@/components/vb";
import { projectsInstance } from "@/services/ProjectsService";
import { useAuth } from "@/context/AuthContext";
import { redirectToStripeWithReturnPath, buildCreditPurchaseReference } from "@/lib/stripeNavigation";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useQueryClient } from "@tanstack/react-query";
import { useReportsStore } from "@/store/store";
import { isBuildingStatus } from "@/lib/reportPeriods";
import type { ReportStatus } from "@/lib/reportPeriods";

const STRIPE_ONE_TIME_CREDIT_URL = import.meta.env.VITE_STRIPE_ONE_TIME_CREDIT_PURCHASE;

interface ReRunReportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    reportId: number;
    /** Company the report belongs to; gates re-running while a build is in flight. */
    companyId?: number;
    /** Called with the new report id after a successful re-run is enqueued. */
    onReRunStarted: (newReportId: number) => void;
}

export const ReRunReportModal: React.FC<ReRunReportModalProps> = ({
    open,
    onOpenChange,
    reportId,
    companyId,
    onReRunStarted,
}) => {
    const { token, user, fetchProfile } = useAuth();
    const { activeWorkspace, refreshWorkspaces } = useWorkspace();
    // Invalidate rather than mounting `useReports`: the shell already runs that
    // hook (AITS-540), and mounting a second copy here started a duplicate 5s
    // poll on every report dashboard.
    const queryClient = useQueryClient();
    const refetchReports = () =>
        queryClient.invalidateQueries({ queryKey: ["reports"] });
    const [submitting, setSubmitting] = useState(false);

    // A run already in flight for this company, from the shell's progress poll.
    // Confirming a re-run then would silently discard the run in progress (the
    // backend replaces the month's report at the start of a run) — the loop our
    // tester fell into four times in one morning. The entry buttons hide while
    // the selected month builds, but they read a list that can be a poll behind;
    // this re-checks at the moment of truth, for any workspace member's run.
    const buildInFlight = useReportsStore((s) =>
        companyId != null &&
        Object.values(s.generatingReports).some(
            (r) =>
                String(r.company_id) === String(companyId) &&
                isBuildingStatus(r.status as ReportStatus),
        ),
    );

    const totalCredits = user?.total_credits ?? user?.credits ?? 0;
    const hasCredits = totalCredits >= 1;

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            const result = await projectsInstance.reRunReport(reportId, token);

            if (result.success && result.reportId) {
                await refetchReports();
                await fetchProfile();
                onOpenChange(false);
                onReRunStarted(result.reportId);
            } else if (result.errorCode === 402) {
                // Server says insufficient credits — refresh profile and let the
                // modal re-render into the "no credits" state.
                await fetchProfile();
                // ...and the workspace's billing account, which is what the
                // sidebar credit chip reads. fetchProfile only refreshes
                // AuthContext.user, so without this the chip keeps showing the
                // pre-402 count until something else reloads workspaces.
                await refreshWorkspaces();
            } else {
                onOpenChange(false);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleBuyCredits = () => {
        onOpenChange(false);
        if (STRIPE_ONE_TIME_CREDIT_URL && user?.email) {
            redirectToStripeWithReturnPath(STRIPE_ONE_TIME_CREDIT_URL, {
                email: user.email,
                returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                clientReferenceId: buildCreditPurchaseReference(
                    activeWorkspace?.id ?? null,
                    "report_modal",
                ),
            });
        }
    };

    // Update-framing on purpose: the run-in-flight and the confirm below talk
    // about "updating this month's report", never about the previous report
    // being replaced — that stays an implementation detail.
    if (buildInFlight) {
        return (
            <Modal
                open={open}
                onOpenChange={onOpenChange}
                showCloseButton={false}
                widthClassName="max-w-sm"
                title="Your report is already being updated"
                description="A report for this company is being generated right now. You can rerun it once this one finishes, usually within a few minutes."
                footer={
                    <VbButton onClick={() => onOpenChange(false)}>Got it</VbButton>
                }
            />
        );
    }

    if (hasCredits) {
        return (
            <Modal
                open={open}
                onOpenChange={onOpenChange}
                showCloseButton={false}
                widthClassName="max-w-sm"
                title="Are you sure you want to rerun the report?"
                description={
                    <>
                        Rerunning re-checks every AI Trust Signal with the latest data and
                        updates this month's report. It takes a few minutes and consumes{" "}
                        <strong>1 credit</strong>.
                    </>
                }
                footer={
                    <>
                        <VbButton
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </VbButton>
                        <VbButton onClick={handleConfirm} disabled={submitting}>
                            {submitting ? "Starting…" : "Rerun Report"}
                        </VbButton>
                    </>
                }
            >
                <p className="text-[12px] leading-relaxed text-vb-text-3">
                    <strong>Note:</strong> Rerunning a report within the same month does not
                    guarantee score changes. Some AI Trust Signals take longer for LLMs to recognize
                    and update their results.
                </p>
            </Modal>
        );
    }

    return (
        <Modal
            open={open}
            onOpenChange={onOpenChange}
            showCloseButton={false}
            widthClassName="max-w-sm"
            title="No credits available"
            footer={
                <>
                    <VbButton variant="secondary" onClick={() => onOpenChange(false)}>
                        Cancel
                    </VbButton>
                    <VbButton onClick={handleBuyCredits}>Buy Credits</VbButton>
                </>
            }
        >
            <Callout tone="warn" icon={<AlertTriangle size={15} />} role="alert">
                You've used all your credits. Purchase additional credits to rerun this report.
            </Callout>
        </Modal>
    );
};

export default ReRunReportModal;
